import React, { Component } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  fetchFilmFestivalEdition,
  fetchFilmFestivalList,
} from "./filmFestivalApi";
import { partitionFestivalEditions } from "./filmFestivalListing";
import { resolveMediaUrl } from "../Helper/mediaUrl";
import ExperienceGate from "../Experience/ExperienceGate";
import "./FilmFestivalPage.css";

/**
 * Public Film Festival hub + edition detail (Phase 3).
 * Festival Pass CTA → existing /pay-per-view for pass AdminVideo.
 * Submit a Film → external FilmFreeway (or Admin-configured) URL only.
 */

function formatWhen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
}

function formatMoney(amount, currency) {
  if (amount == null || amount === "") return null;
  const cur = currency || "USD";
  const n = typeof amount === "number" ? amount : parseFloat(amount);
  if (Number.isNaN(n)) return `${amount} ${cur}`;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: cur,
    }).format(n);
  } catch (e) {
    return `${n.toFixed(2)} ${cur}`;
  }
}

function isLoggedIn() {
  return !!(
    localStorage.getItem("accessToken") && localStorage.getItem("userId")
  );
}

class FilmFestivalPage extends Component {
  state = {
    loading: true,
    edition: null,
    list: [],
    error: false,
    errorMessage: "",
    notFound: false,
    experience: null,
  };

  loadGeneration = 0;

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    const prevSlug =
      (prevProps.forcedSlug != null && prevProps.forcedSlug !== ""
        ? String(prevProps.forcedSlug)
        : null) ||
      (prevProps.match && prevProps.match.params
        ? prevProps.match.params.slug
        : "");
    const nextSlug = this.slug();
    if (prevSlug !== nextSlug) {
      // Legitimate destination change — clear so the prior splash does not linger.
      this.setState({ experience: null });
      this.load();
    }
  }

  slug() {
    if (this.props.forcedSlug != null && this.props.forcedSlug !== "") {
      return String(this.props.forcedSlug);
    }
    return this.props.match && this.props.match.params
      ? this.props.match.params.slug
      : "";
  }

  async load() {
    const slug = this.slug();
    const generation = ++this.loadGeneration;
    // Keep an already-active Experience while destination data reloads.
    // Clearing experience here remounts the splash video from t=0.
    this.setState({
      loading: true,
      error: false,
      errorMessage: "",
      notFound: false,
    });
    if (!slug) {
      const result = await fetchFilmFestivalList();
      if (generation !== this.loadGeneration) {
        return;
      }
      this.setState({
        loading: false,
        list: result.editions || [],
        edition: null,
        error: !!result.error,
        errorMessage: result.message || "",
        notFound: false,
        experience: result.experience || null,
      });
      return;
    }
    const result = await fetchFilmFestivalEdition(slug);
    if (generation !== this.loadGeneration) {
      return;
    }
    if (result.edition) {
      this.setState({
        loading: false,
        edition: result.edition,
        error: false,
        notFound: false,
        errorMessage: "",
        list: [],
        experience:
          (result.edition && result.edition.experience) ||
          result.experience ||
          null,
      });
      return;
    }
    const isNotFound =
      !result.error || /not found/i.test(String(result.message || ""));
    this.setState({
      loading: false,
      edition: null,
      error: !isNotFound,
      notFound: isNotFound,
      errorMessage: result.message || "",
      list: [],
    });
  }

  goPassPurchase = (edition) => {
    const pass = (edition && edition.pass) || {};
    const payload = pass.purchase_payload;
    if (!payload || !payload.admin_video_id) {
      return;
    }
    if (!isLoggedIn()) {
      this.props.history.push("/login");
      return;
    }
    // Reuse existing PPV checkout — no new payment stack.
    this.props.history.push({
      pathname: "/pay-per-view",
      state: { videoDetailsFirst: payload },
    });
  };

  renderEditionLink(ed) {
    return (
      <li className="ffest-list-item" key={ed.slug || ed.id}>
        <Link to={`/film-festival/${ed.slug}`}>{ed.name}</Link>
        {(ed.starts_at || ed.ends_at) && (
          <p className="ffest-meta">
            {[formatWhen(ed.starts_at), formatWhen(ed.ends_at)]
              .filter(Boolean)
              .join(" → ")}
          </p>
        )}
      </li>
    );
  }

  renderHub() {
    const { list } = this.state;
    // Date-window buckets (listing_bucket) — never "all except current" fallback.
    const { nowShowing, upcoming, previous } = partitionFestivalEditions(list);
    const embedded = !!this.props.embedded;

    return (
      <div className={"ffest-page" + (embedded ? " ffest-page--embedded" : "")}>
        {!embedded ? (
          <Helmet>
            <title>Film Festival</title>
          </Helmet>
        ) : null}
        {!embedded ? <p className="ffest-brand">Film Festival</p> : null}
        {!embedded ? <h1 className="ffest-title">Film Festival</h1> : null}
        <p className="ffest-lede">
          Explore the current festival and past editions on Pentecost+.
        </p>

        {list.length === 0 ? (
          <section className="ffest-section" aria-label="Festival editions">
            <h2>Editions</h2>
            <p>No published festivals yet.</p>
          </section>
        ) : null}

        {nowShowing.length > 0 && (
          <section className="ffest-section" aria-label="Current festival">
            <h2>Now showing</h2>
            <ul className="ffest-list">
              {nowShowing.map((ed) => this.renderEditionLink(ed))}
            </ul>
          </section>
        )}

        {upcoming.length > 0 && (
          <section className="ffest-section" aria-label="Upcoming festivals">
            <h2>Upcoming</h2>
            <ul className="ffest-list">
              {upcoming.map((ed) => this.renderEditionLink(ed))}
            </ul>
          </section>
        )}

        {previous.length > 0 && (
          <section className="ffest-section" aria-label="Festival editions">
            <h2>{nowShowing.length > 0 ? "Previous editions" : "Editions"}</h2>
            <ul className="ffest-list">
              {previous.map((ed) => this.renderEditionLink(ed))}
            </ul>
          </section>
        )}
      </div>
    );
  }

  renderEdition(edition) {
    const pass = edition.pass || {};
    const submissions = edition.submissions || {};
    const program = Array.isArray(edition.program) ? edition.program : [];
    const winners = Array.isArray(edition.winners) ? edition.winners : [];
    const banner = resolveMediaUrl(edition.banner_path || edition.logo_path);
    const price = formatMoney(pass.amount, pass.currency);
    const windowLabel = [
      formatWhen(pass.access_starts_at || edition.starts_at),
      formatWhen(pass.access_ends_at || edition.ends_at),
    ]
      .filter(Boolean)
      .join(" → ");
    const embedded = !!this.props.embedded;

    let passCta = null;
    if (pass.user_has_pass) {
      passCta = (
        <span className="ffest-btn is-disabled" aria-disabled="true">
          {edition.programming_unlocked
            ? "Pass active"
            : "Pass purchased — programming unlocks when the festival opens"}
        </span>
      );
    } else if (pass.available && pass.can_purchase && pass.purchase_payload) {
      passCta = (
        <button
          type="button"
          className="ffest-btn"
          onClick={() => this.goPassPurchase(edition)}
        >
          {pass.label || "Festival Pass"}
          {price ? ` · ${price}` : ""}
        </button>
      );
    }

    return (
      <div className={"ffest-page" + (embedded ? " ffest-page--embedded" : "")}>
        {!embedded ? (
          <Helmet>
            <title>{edition.name || "Film Festival"}</title>
          </Helmet>
        ) : null}
        {banner ? (
          <div
            className="ffest-hero"
            style={{ backgroundImage: `url(${banner})` }}
            role="img"
            aria-label={edition.name || "Festival"}
          />
        ) : null}
        {!embedded ? <p className="ffest-brand">Film Festival</p> : null}
        <h1 className="ffest-title">{edition.name}</h1>
        {edition.description ? (
          <p className="ffest-lede">{edition.description}</p>
        ) : null}
        {windowLabel ? (
          <p className="ffest-meta">{windowLabel}</p>
        ) : null}

        <div className="ffest-actions">
          {passCta}
          {submissions.url ? (
            <a
              className="ffest-btn ffest-btn--ghost"
              href={submissions.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {submissions.label || "Submit a Film"}
            </a>
          ) : null}
          <Link className="ffest-btn ffest-btn--ghost" to="/film-festival">
            All festivals
          </Link>
        </div>

        {pass.available && pass.description ? (
          <section className="ffest-section" aria-label="Festival Pass">
            <h2>{pass.label || "Festival Pass"}</h2>
            <p>{pass.description}</p>
            {!pass.within_access_window && pass.user_has_pass ? (
              <p className="ffest-meta">
                Your pass is ready. Programming unlocks during the access
                window.
              </p>
            ) : null}
          </section>
        ) : null}

        {submissions.info ? (
          <section className="ffest-section" aria-label="Submissions">
            <h2>{submissions.label || "Submit a Film"}</h2>
            <p>{submissions.info}</p>
            <p className="ffest-meta">
              Submissions are handled off-platform. Judging is not in-app.
            </p>
          </section>
        ) : null}

        <section className="ffest-section" aria-label="Programming">
          <h2>Programming</h2>
          {program.length === 0 ? (
            <p>Programming will appear here when announced.</p>
          ) : (
            <ul className="ffest-program-list">
              {program.map((item) => {
                const title = item.title || "Program item";
                const videoLink =
                  item.admin_video_id != null
                    ? `/video/${item.admin_video_id}`
                    : item.external_url || null;
                return (
                  <li className="ffest-program-item" key={item.id || item.uuid}>
                    <div>
                      <span className="ffest-program-kind">
                        {item.kind_label || item.kind}
                        {item.is_featured ? " · Featured" : ""}
                      </span>
                      <p className="ffest-program-title">
                        {videoLink ? (
                          <Link to={videoLink}>{title}</Link>
                        ) : (
                          title
                        )}
                      </p>
                      {item.description ? (
                        <p className="ffest-program-desc">{item.description}</p>
                      ) : null}
                    </div>
                    <span className="ffest-badge">
                      {item.pass_unlock_eligible
                        ? "Unlocked with pass"
                        : item.pass_included && item.admin_video_id
                          ? "Pass program"
                          : ""}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {winners.length > 0 ? (
          <section className="ffest-section" aria-label="Winners">
            <h2>Winners</h2>
            <ul className="ffest-list">
              {winners.map((w) => (
                <li className="ffest-list-item" key={w.id}>
                  <strong>{w.display_name || "Winner"}</strong>
                  {w.credit ? ` — ${w.credit}` : ""}
                  {w.is_honorable_mention ? " (Honorable mention)" : ""}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    );
  }

  render() {
    const {
      loading,
      edition,
      error,
      errorMessage,
      notFound,
      experience,
    } = this.state;
    let body = null;
    if (loading) {
      body = (
        <div className="ffest-page">
          <p className="ffest-status">Loading…</p>
        </div>
      );
    } else if (notFound) {
      body = (
        <div className="ffest-page">
          <Helmet>
            <title>Festival not found</title>
          </Helmet>
          <h1 className="ffest-title">Festival not found</h1>
          <p className="ffest-lede">
            This festival may be hidden or the link may be incorrect.
          </p>
          <Link className="ffest-btn ffest-btn--ghost" to="/film-festival">
            Back to Film Festival
          </Link>
        </div>
      );
    } else if (error) {
      body = (
        <div className="ffest-page">
          <p className="ffest-status ffest-status--error">
            {errorMessage || "Something went wrong."}
          </p>
        </div>
      );
    } else if (edition) {
      body = this.renderEdition(edition);
    } else {
      body = this.renderHub();
    }

    return (
      <ExperienceGate
        destinationType="film_festival"
        presentation={experience}
      >
        {body}
      </ExperienceGate>
    );
  }
}

export default FilmFestivalPage;
